import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError, finalize, of, tap } from 'rxjs'

import { ButtonComponent } from '../../../../components/button/button.component'
import { LoadingComponent } from '../../../../components/loading/loading.component'
import { AssessmentService } from '../../../../services/assessment/assesssment.service'
import { SkillMap } from '../../../../services/assessment/assessment.mapping'
import { BiztelIntegrationSetting } from '../../../../services/external-integration/external-integration.mapping'
import { ExternalIntegrationService } from '../../../../services/external-integration/external-integration.service'
import { captureException } from '../../../../util/captureException'
import {
  CustomError,
  CustomErrorCode,
} from '../../../../util/throwIfCustomError'

export type BiztelIntegrationData = {
  setting: BiztelIntegrationSetting | null
}

export type BiztelIntegrationResult = BiztelIntegrationSetting

/** queue_id / 通知番号の入力形式 (数字のみ、先頭 0 許容)。backend validation と同一基準 */
const NUMERIC_ONLY_PATTERN = /^[0-9]+$/

/**
 * queue_id / 通知番号の 1 値あたり最大桁数。backend の BIZTEL_FILTER_LIST_VALUE_MAX_LENGTH と
 * 同一基準。client でも弾かないと、backend の ValidationError が汎用エラーメッセージ
 * (「接続情報を確認してください」) に丸められ原因が伝わらない。
 */
const FILTER_LIST_VALUE_MAX_LENGTH = 20

/**
 * 両リストの合計件数上限。backend の BIZTEL_FILTER_LIST_TOTAL_MAX_COUNT と同一基準。
 * 1 値 = 30 分毎の /queue_log 1 call としてサーバーサイド絞り込みに使われるため、
 * 件数がそのまま BIZTEL API のリクエスト数 quota を消費する (SBL-73)。
 */
const FILTER_LIST_TOTAL_MAX_COUNT = 30

/**
 * 除外する相手電話番号リストの単独の件数上限。backend の
 * BIZTEL_EXCLUDED_EXTERNAL_NUMBER_MAX_COUNT と同一基準。取得後のクライアント側判定のため
 * API call 数を増やさず、上記の合計 30 件 (quota 由来) には含めない (SBL-591)。
 */
const EXCLUDED_EXTERNAL_NUMBER_MAX_COUNT = 100

/**
 * 前後空白を除いて空なら required エラー。Validators.required は length === 0 判定のみで
 * 空白だけの入力を通してしまい、onSubmit の trim で空になった値 (apiToken なら null) が
 * backend の ValidationError → 汎用メッセージに丸められて原因が伝わらない。
 */
const requiredTrimmedValidator = (
  control: AbstractControl<string>,
): ValidationErrors | null =>
  control.value.trim() === '' ? { required: true } : null

@UntilDestroy()
@Component({
  selector: 'app-biztel-integration',
  standalone: true,
  imports: [ReactiveFormsModule, MatIcon, ButtonComponent, LoadingComponent],
  templateUrl: './biztel-integration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './biztel-integration.component.scss',
})
export class BiztelIntegrationComponent implements OnInit {
  skillMaps: SkillMap[] = []
  isLoading = false
  isApiTokenVisible = false
  isEditMode = false
  skillMapLoadError: string | null = null

  fqdn = new FormControl<string>('', {
    nonNullable: true,
    validators: [requiredTrimmedValidator],
  })
  // getBiztelIntegrationSetting は token を返さない (secret)。新規連携では必須だが、
  // 編集時は空欄 = 「現在のトークンを維持」(backend が既存値を据え置く。Kaikura /
  // BlueBean と同方針) とし、リストやスキルマップだけの変更で再入力を求めない。
  // required は ngOnInit で編集モード判定後に外す。
  apiToken = new FormControl<string>('', {
    nonNullable: true,
    validators: [requiredTrimmedValidator],
  })
  skillMapId = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  // 取り込み対象の絞り込みリスト (SBL-73)。チップ型入力 (Kaikura tagAllowlist と同型) で、
  // 0 件 = 当該方向は全件取り込み。保存必須ではない。
  queueIdInput = new FormControl<string>('', { nonNullable: true })
  outboundCallerNumberInput = new FormControl<string>('', { nonNullable: true })
  // 除外する相手電話番号 (SBL-591)。上 2 つの「取り込み対象 (自社側)」と逆に
  // 「相手側番号の除外」であり、0 件 = 除外なし。
  excludedExternalNumberInput = new FormControl<string>('', {
    nonNullable: true,
  })
  queueIds: string[] = []
  outboundCallerNumbers: string[] = []
  excludedExternalNumbers: string[] = []
  /** 保存試行済みフラグ。未追加入力の残存エラーを保存時点から表示する (Kaikura isSubmitted と同型) */
  isSubmitted = false

  // チップ削除でフォーカスが body に落ちるのを防ぎ、対応する入力欄へ戻すための参照
  @ViewChild('queueIdInputEl')
  queueIdInputEl?: ElementRef<HTMLInputElement>
  @ViewChild('outboundCallerNumberInputEl')
  outboundCallerNumberInputEl?: ElementRef<HTMLInputElement>
  @ViewChild('excludedExternalNumberInputEl')
  excludedExternalNumberInputEl?: ElementRef<HTMLInputElement>

  formGroup = this.formBuilder.group({
    fqdn: this.fqdn,
    apiToken: this.apiToken,
    skillMapId: this.skillMapId,
  })

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BiztelIntegrationData,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<BiztelIntegrationComponent>,
    private snackBar: MatSnackBar,
    private externalIntegrationService: ExternalIntegrationService,
    private assessmentService: AssessmentService,
  ) {}

  ngOnInit(): void {
    const setting = this.data.setting
    this.isEditMode = setting?.active === true
    if (this.isEditMode) {
      // 編集時は空欄 = 既存トークン据え置き (backend 側で維持) のため必須だけを外す
      // (clearValidators だと後から足した validator まで編集時に黙って無効になる)
      this.apiToken.removeValidators(requiredTrimmedValidator)
      this.apiToken.updateValueAndValidity()
    }
    if (setting) {
      this.fqdn.setValue(setting.fqdn)
      this.skillMapId.setValue(setting.skillMapId ?? '')
      // 保存済みリストを復元する。ここで通し損ねると FQDN やスキルマップだけの更新で
      // リストが全消去されるため、保存 (onSubmit) まで必ず引き回す
      this.queueIds = [...setting.queueIds]
      this.outboundCallerNumbers = [...setting.outboundCallerNumbers]
      this.excludedExternalNumbers = [...setting.excludedExternalNumbers]
    }

    this.isLoading = true
    this.assessmentService
      .getSkillMaps()
      .pipe(
        tap((skillMaps) => (this.skillMaps = skillMaps)),
        catchError((error: unknown) => {
          captureException(error)
          // スキルマップは必須項目のため、取得失敗時はドロップダウンが空のまま
          // 保存できず手詰まりになる。消える snackBar ではなくダイアログ内に
          // 持続表示し、再読込導線を伝える (Kaikura / BlueBean と同型)。
          this.skillMapLoadError =
            'スキルマップの取得に失敗しました。ページを再読込してください。'
          return of([])
        }),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  onSubmit(event: Event): void {
    event.preventDefault()
    this.formGroup.markAllAsTouched()
    this.isSubmitted = true

    if (this.formGroup.invalid) {
      this.snackBar.open('入力内容を確認してください。', '閉じる', {
        duration: 4000,
      })
      return
    }

    // 入力欄に残った未追加の値は silent に破棄せず保存を止める。追加し忘れのまま保存すると
    // 「絞り込んだつもりが全件取り込みのまま」という fail-open になり、保存成功の snackBar で
    // 気付く契機も失われるため (インラインエラーは isSubmitted 後にテンプレート側で表示)
    if (this.hasPendingListInput()) {
      this.snackBar.open(
        '追加されていない入力値があります。「追加」を押すか、入力欄を空にしてください。',
        '閉じる',
        { duration: 6000 },
      )
      return
    }

    this.isLoading = true
    this.externalIntegrationService
      .saveBiztelIntegration({
        fqdn: this.fqdn.value.trim(),
        // 編集時の空欄は null で送り「現在のトークンを維持」を backend に伝える
        apiToken:
          this.apiToken.value.trim() === '' ? null : this.apiToken.value.trim(),
        skillMapId: this.skillMapId.value,
        active: true,
        queueIds: this.queueIds,
        outboundCallerNumbers: this.outboundCallerNumbers,
        // 空配列も「全解除」の明示値として常に送る (省略 = 既存値維持は旧 client 互換用)
        excludedExternalNumbers: this.excludedExternalNumbers,
      })
      .pipe(
        tap(() => {
          this.snackBar.open('BIZTEL 連携を保存しました。', '閉じる', {
            duration: 4000,
          })
          // saveBiztelIntegration は Boolean! を返すため、保存値から楽観的に
          // setting を組み立てて呼び出し元に返す (Kaikura と同型)。fqdn は
          // backend で正規化されるが、次回 fetch で正規化値に揃う。
          this.dialogRef.close({
            active: true,
            fqdn: this.fqdn.value.trim(),
            skillMapId: this.skillMapId.value,
            queueIds: this.queueIds,
            outboundCallerNumbers: this.outboundCallerNumbers,
            excludedExternalNumbers: this.excludedExternalNumbers,
            lastSyncedUpdateDatetime:
              this.data.setting?.lastSyncedUpdateDatetime ?? null,
          } satisfies BiztelIntegrationResult)
        }),
        catchError((error: unknown) => {
          captureException(error)
          this.snackBar.open(this.toErrorMessage(error), '閉じる', {
            duration: 6000,
          })
          return of(null)
        }),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  close(): void {
    this.dialogRef.close()
  }

  /**
   * 両リスト合計の上限到達判定。到達中は両欄とも追加不可 (どちらに足しても合計超過のため)。
   * 上限の理由は FILTER_LIST_TOTAL_MAX_COUNT のコメント参照。
   */
  isFilterListTotalAtLimit(): boolean {
    return (
      this.queueIds.length + this.outboundCallerNumbers.length >=
      FILTER_LIST_TOTAL_MAX_COUNT
    )
  }

  /** 入力欄に残った未追加の値があるか (保存前のガードとインラインエラー表示に使う) */
  hasPendingListInput(): boolean {
    return (
      this.trimmedQueueIdInput !== '' ||
      this.trimmedOutboundCallerNumberInput !== '' ||
      this.trimmedExcludedExternalNumberInput !== ''
    )
  }

  // --- 対象キューID (着信) チップ入力。Kaikura addTag/removeTag と同型 + 数字バリデーション ---

  get trimmedQueueIdInput(): string {
    return this.queueIdInput.value.trim()
  }

  addQueueId(): void {
    const value = this.trimmedQueueIdInput
    if (!value) {
      this.queueIdInput.setValue('')
      return
    }
    if (
      !NUMERIC_ONLY_PATTERN.test(value) ||
      value.length > FILTER_LIST_VALUE_MAX_LENGTH ||
      this.queueIds.includes(value) ||
      this.isFilterListTotalAtLimit()
    ) {
      return
    }
    this.queueIds = [...this.queueIds, value]
    this.queueIdInput.setValue('')
  }

  removeQueueId(value: string): void {
    this.queueIds = this.queueIds.filter((v) => v !== value)
    // 削除ボタン自身が DOM から消えフォーカスが body に落ちるため、入力欄へ戻す
    // (キーボード操作でチップを連続削除できるように)
    this.queueIdInputEl?.nativeElement.focus()
  }

  isQueueIdInputInvalid(): boolean {
    return (
      this.trimmedQueueIdInput !== '' &&
      !NUMERIC_ONLY_PATTERN.test(this.trimmedQueueIdInput)
    )
  }

  isQueueIdInputTooLong(): boolean {
    return this.trimmedQueueIdInput.length > FILTER_LIST_VALUE_MAX_LENGTH
  }

  isQueueIdDuplicated(): boolean {
    return (
      this.trimmedQueueIdInput !== '' &&
      this.queueIds.includes(this.trimmedQueueIdInput)
    )
  }

  isAddQueueIdDisabled(): boolean {
    return (
      this.trimmedQueueIdInput === '' ||
      this.isQueueIdInputInvalid() ||
      this.isQueueIdInputTooLong() ||
      this.isQueueIdDuplicated() ||
      this.isFilterListTotalAtLimit()
    )
  }

  // --- 対象通知番号 (発信) チップ入力 ---

  get trimmedOutboundCallerNumberInput(): string {
    return this.outboundCallerNumberInput.value.trim()
  }

  addOutboundCallerNumber(): void {
    const value = this.trimmedOutboundCallerNumberInput
    if (!value) {
      this.outboundCallerNumberInput.setValue('')
      return
    }
    if (
      !NUMERIC_ONLY_PATTERN.test(value) ||
      value.length > FILTER_LIST_VALUE_MAX_LENGTH ||
      this.outboundCallerNumbers.includes(value) ||
      this.isFilterListTotalAtLimit()
    ) {
      return
    }
    this.outboundCallerNumbers = [...this.outboundCallerNumbers, value]
    this.outboundCallerNumberInput.setValue('')
  }

  removeOutboundCallerNumber(value: string): void {
    this.outboundCallerNumbers = this.outboundCallerNumbers.filter(
      (v) => v !== value,
    )
    this.outboundCallerNumberInputEl?.nativeElement.focus()
  }

  isOutboundCallerNumberInputInvalid(): boolean {
    return (
      this.trimmedOutboundCallerNumberInput !== '' &&
      !NUMERIC_ONLY_PATTERN.test(this.trimmedOutboundCallerNumberInput)
    )
  }

  isOutboundCallerNumberInputTooLong(): boolean {
    return (
      this.trimmedOutboundCallerNumberInput.length >
      FILTER_LIST_VALUE_MAX_LENGTH
    )
  }

  isOutboundCallerNumberDuplicated(): boolean {
    return (
      this.trimmedOutboundCallerNumberInput !== '' &&
      this.outboundCallerNumbers.includes(this.trimmedOutboundCallerNumberInput)
    )
  }

  isAddOutboundCallerNumberDisabled(): boolean {
    return (
      this.trimmedOutboundCallerNumberInput === '' ||
      this.isOutboundCallerNumberInputInvalid() ||
      this.isOutboundCallerNumberInputTooLong() ||
      this.isOutboundCallerNumberDuplicated() ||
      this.isFilterListTotalAtLimit()
    )
  }

  // --- 除外する相手電話番号 チップ入力 (SBL-591)。上 2 リストと同型だが、件数上限は
  // 合計 30 件 (quota 由来) ではなく単独 100 件 (API call を増やさないため) ---

  get trimmedExcludedExternalNumberInput(): string {
    return this.excludedExternalNumberInput.value.trim()
  }

  addExcludedExternalNumber(): void {
    const value = this.trimmedExcludedExternalNumberInput
    if (!value) {
      this.excludedExternalNumberInput.setValue('')
      return
    }
    if (
      !NUMERIC_ONLY_PATTERN.test(value) ||
      value.length > FILTER_LIST_VALUE_MAX_LENGTH ||
      this.excludedExternalNumbers.includes(value) ||
      this.isExcludedExternalNumberAtLimit()
    ) {
      return
    }
    this.excludedExternalNumbers = [...this.excludedExternalNumbers, value]
    this.excludedExternalNumberInput.setValue('')
  }

  removeExcludedExternalNumber(value: string): void {
    this.excludedExternalNumbers = this.excludedExternalNumbers.filter(
      (v) => v !== value,
    )
    this.excludedExternalNumberInputEl?.nativeElement.focus()
  }

  isExcludedExternalNumberInputInvalid(): boolean {
    return (
      this.trimmedExcludedExternalNumberInput !== '' &&
      !NUMERIC_ONLY_PATTERN.test(this.trimmedExcludedExternalNumberInput)
    )
  }

  isExcludedExternalNumberInputTooLong(): boolean {
    return (
      this.trimmedExcludedExternalNumberInput.length >
      FILTER_LIST_VALUE_MAX_LENGTH
    )
  }

  isExcludedExternalNumberDuplicated(): boolean {
    return (
      this.trimmedExcludedExternalNumberInput !== '' &&
      this.excludedExternalNumbers.includes(
        this.trimmedExcludedExternalNumberInput,
      )
    )
  }

  isExcludedExternalNumberAtLimit(): boolean {
    return (
      this.excludedExternalNumbers.length >= EXCLUDED_EXTERNAL_NUMBER_MAX_COUNT
    )
  }

  isAddExcludedExternalNumberDisabled(): boolean {
    return (
      this.trimmedExcludedExternalNumberInput === '' ||
      this.isExcludedExternalNumberInputInvalid() ||
      this.isExcludedExternalNumberInputTooLong() ||
      this.isExcludedExternalNumberDuplicated() ||
      this.isExcludedExternalNumberAtLimit()
    )
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof CustomError) {
      if (error.code === CustomErrorCode.INVALID_BIZTEL_TOKEN) {
        return 'API トークンが不正です。BIZTEL の API トークンを確認してください。'
      }
      if (error.code === CustomErrorCode.BIZTEL_CONNECTION_ERROR) {
        return 'BIZTEL への接続に失敗しました。FQDN・API トークン・録音ダウンロード権限を確認してください。'
      }
    }
    return 'BIZTEL 連携の保存に失敗しました。接続情報を確認してください。'
  }
}
